import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useParams } from "react-router-dom";

import dayjs from "dayjs";

//* Types
import { CustomDate, GqlClassRoom, GqlStudent } from "../types";

import { ROUTES } from "../constants/routes";
import { useTranslationsContext } from "./TranslationsContext";
import { pureSortStudents } from "../utils/students";

type AttendanceContextValues = {
  classRoom: Partial<GqlClassRoom> | undefined;
  classRooms: Partial<GqlClassRoom>[];
  students: Partial<GqlStudent>[];
  student: Partial<GqlStudent> | undefined;
  startDate: CustomDate; // It will be max 6 months cause every 6 months the get diplomas
  endDate: CustomDate; // dateToday always
  limit: number;
  offset: number;
  breadcrumbs: [];
  qualificationDate: string | undefined;
  refetchStudentTimelineData: boolean;
  setClassRoom(classRoom: Partial<GqlClassRoom> | undefined): void;
  setClassRooms(classRooma: Partial<GqlClassRoom>[] | undefined): void;
  setStudents(students: Partial<GqlStudent>[] | undefined): void;
  setCurrentStudent(student: Partial<GqlStudent> | undefined): void;
  setStartDate(date: CustomDate): void;
  setEndDate(date: CustomDate): void;
  setRefetchStudentTimelineData(refetch: boolean): void;
  setQualificationDate(date: string | undefined): void;
  setBreadcrumbs(breadcrumbs: any[]): void;
};
const defaultValues: AttendanceContextValues = {
  classRooms: [],
  students: [],
  classRoom: {
    id: "",
    name: "",
    subject: "",
  },
  student: {
    id: "",
    name: "",
  },
  breadcrumbs: [],
  qualificationDate: undefined,
  startDate: dayjs().subtract(1, "month").startOf("day"),
  endDate: dayjs().startOf("day"),
  limit: 0,
  offset: 0,
  refetchStudentTimelineData: false,
  setClassRoom: () => {},
  setClassRooms: () => {},
  setStudents: () => {},
  setCurrentStudent: () => {},
  setStartDate: () => {},
  setEndDate: () => {},
  setRefetchStudentTimelineData: () => {},
  setQualificationDate: () => {},
  setBreadcrumbs: () => {},
};

export const AttendanceContext =
  createContext<AttendanceContextValues>(defaultValues);

export function useAttendanceContext() {
  return useContext(AttendanceContext);
}

type AttendanceProviderProps = {
  children: React.ReactNode;
};
export function AttendanceProvider({
  children,
}: AttendanceProviderProps): JSX.Element {
  const { translation } = useTranslationsContext();
  const { classId, studentId } = useParams();
  const location = useLocation();
  const [refetchStudentTimelineData, setRefetchStudentTimelineData] =
    useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState(location?.state?.breadcrumbs);
  const [qualificationDate, setQualificationDate] = useState<
    string | undefined
  >(location?.state?.qualificationDate);
  const [classRooms, setClassRooms] = useState<Partial<GqlClassRoom>[]>(
    defaultValues.classRooms
  );
  const [students, setStudents] = useState<Partial<GqlStudent>[]>(
    defaultValues.students
  );
  const [classRoom, setClassRoom] = useState<Partial<GqlClassRoom> | undefined>({
    ...defaultValues.classRoom,
    id: classId || defaultValues.classRoom.id,
    name: classRooms.find((item) => item.id === classId)?.name || "",
    subject: classRooms.find((item) => item.id === classId)?.subject || "",
  });
  const [currentStudent, setCurrentStudent] = useState<
    Partial<GqlStudent> | undefined
  >({
    ...defaultValues.student,
    id: studentId || defaultValues.student.id,
  });
  const [startDate, setStartDate] = useState<CustomDate>(
    defaultValues.startDate
  );
  const [endDate, setEndDate] = useState<CustomDate>(defaultValues.endDate);

  const updateStudents = useCallback((studentsFromBE) => {
    const sortedStudents = pureSortStudents(studentsFromBE);
    setStudents(sortedStudents);
  }, []);

  const updateQualificationDate = useCallback((date) => {
    setQualificationDate(date);
  }, []);

  useEffect(() => {
    if (location?.state?.breadcrumbs) {
      setBreadcrumbs(location?.state?.breadcrumbs);
    }
  }, [location?.state?.breadcrumbs]);

  useEffect(() => {
    const classLabel = classRoom?.name
      ? `${translation.CLASS_ROOM.defaultMessage}: ${classRoom?.name} - ${classRoom?.subject}`
      : translation.CLASS_ROOM.defaultMessage;

    const INIT_BREADCRUMBS = [
      {
        id: 0,
        label: translation.CLASS_ROOMS.defaultMessage,
        path: ROUTES.HOME,
        icon: "HomeIcon",
      },
      {
        id: 1,
        label: classLabel,
        path: `/class/${classId}`,
        icon: "AppsIcon",
      },
      {
        id: 2,
        label: "ParticiFy",
        path: `/class-attendance/${classId}`,
        icon: "Groups2Icon",
      },
    ];

    if (
      students?.length === 0 &&
      breadcrumbs === undefined &&
      classRoom?.name &&
      classRoom?.subject
    ) {
      setBreadcrumbs(INIT_BREADCRUMBS);
    } else if (
      students.length > 0 &&
      breadcrumbs === undefined &&
      currentStudent?.name
    ) {
      const initBreadcrumbs = [
        ...INIT_BREADCRUMBS,
        {
          id: 3,
          label: `${translation.STUDENT.defaultMessage}: ${currentStudent?.name}`,
          path: `/class-attendance/${classId}/student-attendance/${studentId}`,
          icon: "GirlIcon",
        },
      ];
      setBreadcrumbs(initBreadcrumbs);
    }
  }, [
    breadcrumbs,
    classId,
    studentId,
    classRoom?.name,
    classRoom?.subject,
    currentStudent?.name,
    students.length,
    translation,
  ]);

  const value = useMemo(
    () => ({
      classRoom,
      startDate,
      endDate,
      student: currentStudent,
      classRooms,
      students,
      breadcrumbs,
      qualificationDate,
      refetchStudentTimelineData,
      setRefetchStudentTimelineData,
      setStudents: updateStudents,
      setClassRooms,
      setClassRoom,
      setCurrentStudent,
      setEndDate,
      setStartDate,
      setQualificationDate: updateQualificationDate,
      setBreadcrumbs,
      limit: 0,
      offset: 0,
    }),
    [
      breadcrumbs,
      classRoom,
      classRooms,
      currentStudent,
      endDate,
      qualificationDate,
      refetchStudentTimelineData,
      startDate,
      students,
      updateQualificationDate,
      updateStudents,
    ]
  );

  return (
    <AttendanceContext.Provider value={value}>
      {children}
    </AttendanceContext.Provider>
  );
}
